import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { progressStore } from '../services/progressStore';
import { testLoaderService } from '../services/testLoaderService';
import { TestData, TestAttempt } from '../types/testy';
import { Spinner } from './LoadingComponents';

interface ResultsSummaryProps {
  testId: string;
  onNavigate: (section: string, testId?: string) => void;
}

const ResultsSummary: React.FC<ResultsSummaryProps> = ({ testId, onNavigate }) => {
  const { currentUser } = useAuth();
  const [testData, setTestData] = useState<TestData | null>(null);
  const [testAttempt, setTestAttempt] = useState<TestAttempt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadResults = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Load test data
        const data = await testLoaderService.loadTest(testId);
        setTestData(data);

        // Load the most recent completed attempt
        const history = await progressStore.getTestHistory(currentUser.uid);
        const completedAttempt = history.find(attempt => 
          attempt.testId === testId && attempt.isCompleted && attempt.scores
        );

        if (!completedAttempt) {
          throw new Error('No completed test attempt found');
        }

        setTestAttempt(completedAttempt);
      } catch (err) {
        console.error('Failed to load test results:', err);
        setError(err instanceof Error ? err.message : 'Failed to load test results');
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [testId, currentUser]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getPercentileBadgeColor = (percentile: number): string => {
    if (percentile >= 90) return 'bg-green-500 shadow-green-200 dark:shadow-green-900';
    if (percentile >= 75) return 'bg-blue-500 shadow-blue-200 dark:shadow-blue-900';
    if (percentile >= 50) return 'bg-yellow-500 shadow-yellow-200 dark:shadow-yellow-900';
    if (percentile >= 25) return 'bg-orange-500 shadow-orange-200 dark:shadow-orange-900';
    return 'bg-red-500 shadow-red-200 dark:shadow-red-900';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-2 font-poppins">
          Chyba při načítání výsledků
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins mb-4">
          {error}
        </p>
        <button
          onClick={() => onNavigate('test-list')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200"
        >
          Zpět na seznam testů
        </button>
      </div>
    );
  }

  if (!testData || !testAttempt || !testAttempt.scores) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❓</div>
        <h2 className="text-2xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
          Výsledky nenalezeny
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins mb-4">
          Výsledky testu nebyly nalezeny nebo nejsou dostupné.
        </p>
        <button
          onClick={() => onNavigate('test-list')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200"
        >
          Zpět na seznam testů
        </button>
      </div>
    );
  }

  const scores = testAttempt.scores;
  const primaryPercentile = scores.overallPercentile ?? scores.estimatedPercentile;
  const timeSpent = testAttempt.startTime && testAttempt.endTime 
    ? Math.floor((testAttempt.endTime.getTime() - testAttempt.startTime.getTime()) / 1000)
    : testData.durationSec - testAttempt.timeRemaining;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <header className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
            Výsledky testu
          </h1>
          <h2 className="text-lg sm:text-xl text-light-text-secondary dark:text-dark-text-secondary font-poppins">
            {testData.title}
          </h2>
        </header>

        {/* Primary Percentile Badge */}
        {primaryPercentile !== undefined && (
          <section className="text-center mb-8" aria-labelledby="percentile-heading">
            <div 
              className={`inline-flex items-center justify-center w-24 h-24 sm:w-32 sm:h-32 rounded-full ${getPercentileBadgeColor(primaryPercentile)} text-white mb-4 shadow-lg`}
              role="img"
              aria-label={`${Math.round(primaryPercentile)} percentil`}
            >
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold">{Math.round(primaryPercentile)}</div>
                <div className="text-xs sm:text-sm opacity-90">percentil</div>
              </div>
            </div>
            <h2 
              id="percentile-heading"
              className="text-light-text-secondary dark:text-dark-text-secondary font-medium"
            >
              {scores.overallPercentile !== undefined ? 'Celkový percentil' : 'Odhadovaný percentil'}
            </h2>
            {scores.overallPercentile === undefined && (
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1 opacity-75">
                Průměr percentilů jednotlivých sekcí
              </p>
            )}
          </section>
        )}

        {/* Score Overview */}
        <section 
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6"
          aria-labelledby="score-overview-title"
        >
          <h3 
            id="score-overview-title"
            className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 font-poppins"
          >
            Celkové skóre
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <div 
                className="text-xl sm:text-2xl font-bold text-light-text-primary dark:text-dark-text-primary"
                aria-label={`${scores.totalPoints.toFixed(1)} bodů z ${scores.maxTotalPoints} možných`}
              >
                {scores.totalPoints.toFixed(1)} / {scores.maxTotalPoints}
              </div>
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Celkem bodů
              </div>
            </div>
            <div className="text-center">
              <div 
                className="text-xl sm:text-2xl font-bold text-light-text-primary dark:text-dark-text-primary"
                aria-label={`${scores.totalPercentage.toFixed(1)} procent úspěšnost`}
              >
                {scores.totalPercentage.toFixed(1)}%
              </div>
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Úspěšnost
              </div>
            </div>
            <div className="text-center">
              <div 
                className="text-xl sm:text-2xl font-bold text-light-text-primary dark:text-dark-text-primary"
                aria-label={`Čas testu: ${formatTime(timeSpent)}`}
              >
                {formatTime(timeSpent)}
              </div>
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Čas testu
              </div>
            </div>
            <div className="text-center">
              <div 
                className="text-xl sm:text-2xl font-bold text-light-text-primary dark:text-dark-text-primary"
                aria-label={`${Object.keys(testAttempt.answers).length} otázek zodpovězeno`}
              >
                {Object.keys(testAttempt.answers).length}
              </div>
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Zodpovězeno
              </div>
            </div>
          </div>
        </section>

        {/* Section Cards */}
        <section 
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6"
          aria-labelledby="section-results-title"
        >
          <h3 id="section-results-title" className="sr-only">Výsledky podle sekcí</h3>
          {scores.sections.map((section) => {
            const sectionData = testData.sections.find(s => s.id === section.sectionId);
            const totalQuestions = section.parts.reduce((sum, part) => sum + part.correct + part.wrong + part.blank, 0);
            const answeredQuestions = section.parts.reduce((sum, part) => sum + part.correct + part.wrong, 0);
            
            return (
              <article 
                key={section.sectionId} 
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200"
                aria-labelledby={`section-${section.sectionId}-title`}
              >
                <h4 
                  id={`section-${section.sectionId}-title`}
                  className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 font-poppins text-base sm:text-lg"
                >
                  {sectionData?.title || section.sectionId}
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-light-text-secondary dark:text-dark-text-secondary">Body:</span>
                    <span 
                      className="font-bold text-light-text-primary dark:text-dark-text-primary text-base sm:text-lg"
                      aria-label={`${section.points.toFixed(1)} bodů z ${section.maxPoints} možných`}
                    >
                      {section.points.toFixed(1)} / {section.maxPoints}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-light-text-secondary dark:text-dark-text-secondary">Úspěšnost:</span>
                    <span 
                      className="font-medium text-light-text-primary dark:text-dark-text-primary"
                      aria-label={`${section.percentage.toFixed(1)} procent úspěšnost`}
                    >
                      {section.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-light-text-secondary dark:text-dark-text-secondary">Zodpovězeno:</span>
                    <span 
                      className="font-medium text-light-text-primary dark:text-dark-text-primary"
                      aria-label={`${answeredQuestions} z ${totalQuestions} otázek zodpovězeno`}
                    >
                      {answeredQuestions} / {totalQuestions}
                    </span>
                  </div>
                  {section.percentile !== undefined && (
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-600">
                      <span className="text-light-text-secondary dark:text-dark-text-secondary font-medium">Percentil:</span>
                      <span 
                        className={`font-bold text-base sm:text-lg px-2 py-1 rounded ${getPercentileBadgeColor(section.percentile).split(' ')[0]} text-white`}
                        aria-label={`${Math.round(section.percentile)} percentil`}
                      >
                        {Math.round(section.percentile)}
                      </span>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>

        {/* Detailed Parts Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 font-poppins">
            Detailní výsledky podle částí
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 dark:border-gray-600">
                  <th className="text-left py-3 px-2 text-light-text-primary dark:text-dark-text-primary font-semibold">Část</th>
                  <th className="text-center py-3 px-2 text-light-text-primary dark:text-dark-text-primary font-semibold">Body</th>
                  <th className="text-center py-3 px-2 text-green-600 dark:text-green-400 font-semibold">Správně</th>
                  <th className="text-center py-3 px-2 text-red-600 dark:text-red-400 font-semibold">Špatně</th>
                  <th className="text-center py-3 px-2 text-gray-500 dark:text-gray-400 font-semibold">Nevyplněno</th>
                  <th className="text-center py-3 px-2 text-light-text-primary dark:text-dark-text-primary font-semibold">Celkem</th>
                </tr>
              </thead>
              <tbody>
                {scores.sections.map((section, sectionIndex) => 
                  section.parts.map((part, partIndex) => {
                    const sectionData = testData.sections.find(s => s.id === section.sectionId);
                    const partData = sectionData?.parts.find(p => p.id === part.partId);
                    const totalQuestions = part.correct + part.wrong + part.blank;
                    const isFirstPartInSection = partIndex === 0;
                    
                    return (
                      <tr key={part.partId} className={`border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 ${isFirstPartInSection && sectionIndex > 0 ? 'border-t-2 border-gray-200 dark:border-gray-600' : ''}`}>
                        <td className="py-3 px-2 text-light-text-primary dark:text-dark-text-primary">
                          <div className="font-medium">
                            {partData?.title || part.partId}
                          </div>
                          <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                            {sectionData?.title}
                          </div>
                        </td>
                        <td className="text-center py-3 px-2">
                          <span className="font-bold text-light-text-primary dark:text-dark-text-primary">
                            {part.points.toFixed(1)}
                          </span>
                          <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                            / {part.maxPoints}
                          </div>
                        </td>
                        <td className="text-center py-3 px-2">
                          <span className="font-bold text-green-600 dark:text-green-400 text-lg">
                            {part.correct}
                          </span>
                        </td>
                        <td className="text-center py-3 px-2">
                          <span className="font-bold text-red-600 dark:text-red-400 text-lg">
                            {part.wrong}
                          </span>
                        </td>
                        <td className="text-center py-3 px-2">
                          <span className="font-bold text-gray-500 dark:text-gray-400 text-lg">
                            {part.blank}
                          </span>
                        </td>
                        <td className="text-center py-3 px-2">
                          <span className="font-medium text-light-text-primary dark:text-dark-text-primary">
                            {totalQuestions}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Summary Row */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-5 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">
                  {scores.totalPoints.toFixed(1)}
                </div>
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  Celkem bodů
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {scores.sections.reduce((sum, section) => 
                    sum + section.parts.reduce((partSum, part) => partSum + part.correct, 0), 0
                  )}
                </div>
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  Správně celkem
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {scores.sections.reduce((sum, section) => 
                    sum + section.parts.reduce((partSum, part) => partSum + part.wrong, 0), 0
                  )}
                </div>
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  Špatně celkem
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-500 dark:text-gray-400">
                  {scores.sections.reduce((sum, section) => 
                    sum + section.parts.reduce((partSum, part) => partSum + part.blank, 0), 0
                  )}
                </div>
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  Nevyplněno celkem
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">
                  {scores.sections.reduce((sum, section) => 
                    sum + section.parts.reduce((partSum, part) => partSum + part.correct + part.wrong + part.blank, 0), 0
                  )}
                </div>
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  Celkem otázek
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <nav className="flex flex-col sm:flex-row gap-4 justify-center" role="navigation" aria-label="Akce po dokončení testu">
          <button
            onClick={() => onNavigate('test-review', testId)}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            Prohlédnout odpovědi
          </button>
          <button
            onClick={() => onNavigate('test-list')}
            className="px-6 py-3 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500 focus:bg-gray-300 dark:focus:bg-gray-500 rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            Zpět na seznam testů
          </button>
        </nav>
      </div>
    </div>
  );
};

export default ResultsSummary;